import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, User } from 'lucide-react';
import { Input } from '@/components/ui';
import api from '@/services/api';
import { ApiResponse, Customer, PagedResponse } from '@/services/types';
import { API_ENDPOINTS, DEBOUNCE_DELAY } from '@/utils/constants';

interface Props {
    shopId: number;
    name: string;
    phone: string;
    onChange: (next: { name: string; phone: string }) => void;
    required?: boolean;
    /** Landscape panel: user / phone icons instead of labels */
    icons?: boolean;
}

/**
 * Customer name/phone inputs backed by a native <datalist> fed from customers/search.
 * Picking a suggestion fills both fields.
 */
export const CustomerAutocomplete: React.FC<Props> = ({ shopId, name, phone, onChange, required, icons }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Customer[]>([]);
    const listId = useMemo(() => `customers-${shopId}`, [shopId]);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); return; }
        const timer = setTimeout(async () => {
            try {
                const res = await api.get<ApiResponse<PagedResponse<Customer>>>(API_ENDPOINTS.CUSTOMER_SEARCH(shopId), {
                    params: { q, page: 0, size: 10 },
                });
                setResults(res.data.data.content || []);
            } catch { setResults([]); }
        }, DEBOUNCE_DELAY);
        return () => clearTimeout(timer);
    }, [query, shopId]);

    const label = (c: Customer) => `${c.name || '-'} · ${c.phone}`;

    const pick = (value: string, field: 'name' | 'phone') => {
        const hit = results.find((c) => label(c) === value);
        if (hit) {
            onChange({ name: hit.name || '', phone: hit.phone });
            setQuery('');
            return;
        }
        setQuery(value);
        onChange(field === 'name' ? { name: value, phone } : { name, phone: value });
    };

    return (
        <>
            <datalist id={listId}>
                {results.map((c) => (
                    <option key={c.id} value={label(c)}>
                        {t('cart.visits', { count: c.visitCount })}
                    </option>
                ))}
            </datalist>
            <Input
                label={t('cart.customerName')}
                leftIcon={icons ? <User /> : undefined}
                placeholder={t('cart.enterCustomerName')}
                value={name}
                list={listId}
                autoComplete="off"
                required={required}
                onChange={(e) => pick(e.target.value, 'name')}
            />
            <Input
                label={t('cart.customerPhone')}
                leftIcon={icons ? <Phone /> : undefined}
                type="tel"
                placeholder={t('cart.enterCustomerPhone')}
                value={phone}
                list={listId}
                autoComplete="off"
                required={required}
                onChange={(e) => pick(e.target.value, 'phone')}
            />
        </>
    );
};

export default CustomerAutocomplete;
